/**
 * Shared validation utilities for consistent validation across the application
 */

import i18n from '../i18n';

/**
 * Standard email validation regex pattern
 */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validates if an email address is in the correct format
 * @param email - The email address to validate
 * @returns true if the email format is valid, false otherwise
 */
export const isValidEmail = (email: string): boolean => {
  return EMAIL_REGEX.test(email);
};

/**
 * Validates if a password meets minimum requirements
 * @param password - The password to validate
 * @returns true if the password meets requirements, false otherwise
 */
export const isValidPassword = (password: string): boolean => {
  // Minimum 6 characters
  return password.length >= 6;
};

/**
 * Validates if a string is not empty or just whitespace
 * @param value - The string to validate
 * @returns true if the string has content, false otherwise
 */
export const isNotEmpty = (value: string): boolean => {
  return value.trim().length > 0;
};

/**
 * Validates if a phone number is in a valid format
 * @param phone - The phone number to validate
 * @returns true if the phone number format is valid, false otherwise
 */
export const isValidPhone = (phone: string): boolean => {
  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, '');
  // Check if it's between 10-15 digits (international format)
  return digitsOnly.length >= 10 && digitsOnly.length <= 15;
};

/**
 * Common validation error messages.
 * Implemented with getters so each access returns text in the active language.
 */
export const VALIDATION_MESSAGES = {
  get EMAIL_REQUIRED() { return i18n.t('validation.email_required'); },
  get EMAIL_INVALID() { return i18n.t('validation.email_invalid'); },
  get PASSWORD_REQUIRED() { return i18n.t('validation.password_required'); },
  get PASSWORD_TOO_SHORT() { return i18n.t('validation.password_too_short'); },
  get FIELD_REQUIRED() { return i18n.t('validation.field_required'); },
  get PHONE_INVALID() { return i18n.t('validation.phone_invalid'); },
} as const;

// ============================================================================
// Enhanced Validation Functions for Onboarding Flow
// ============================================================================

/**
 * Validates an email address for the onboarding form
 * @param email - The email address to validate
 * @returns null if valid, error message if invalid
 */
export const validateEmail = (email: string): string | null => {
  if (!email || email.trim().length === 0) {
    return i18n.t('validation.email_required_short');
  }
  if (!EMAIL_REGEX.test(email)) {
    return i18n.t('validation.email_invalid_long');
  }
  return null;
};

/**
 * Validates a password for the onboarding form
 * Requires: min 8 characters, uppercase, lowercase, number
 * @param password - The password to validate
 * @returns null if valid, error message if invalid
 */
export const validatePassword = (password: string): string | null => {
  if (!password || password.length === 0) {
    return i18n.t('validation.password_required_short');
  }
  if (password.length < 8) {
    return i18n.t('validation.password_min_8');
  }
  if (!/[A-Z]/.test(password)) {
    return i18n.t('validation.password_uppercase');
  }
  if (!/[a-z]/.test(password)) {
    return i18n.t('validation.password_lowercase');
  }
  if (!/[0-9]/.test(password)) {
    return i18n.t('validation.password_number');
  }
  return null;
};

/**
 * Validates a name field (first name or last name)
 * @param name - The name to validate
 * @param fieldName - The field name for error messages (e.g., "First name")
 * @returns null if valid, error message if invalid
 */
// Map English field labels passed by callers to localized labels so the
// interpolated validation messages read naturally in every language.
const localizeFieldName = (fieldName: string): string => {
  const map: Record<string, string> = {
    'First name': 'validation.field_first_name',
    'Last name': 'validation.field_last_name',
    Name: 'validation.field_name',
  };
  const key = map[fieldName];
  return key ? i18n.t(key) : fieldName;
};

export const validateName = (name: string, fieldName: string = 'Name'): string | null => {
  const field = localizeFieldName(fieldName);
  if (!name || name.trim().length === 0) {
    return i18n.t('validation.name_required', { field });
  }
  if (name.trim().length < 2) {
    return i18n.t('validation.name_min_2', { field });
  }
  if (name.trim().length > 50) {
    return i18n.t('validation.name_max_50', { field });
  }
  if (!/^[a-zA-Z\s\-']+$/.test(name)) {
    return i18n.t('validation.name_chars', { field });
  }
  return null;
};

/**
 * Validates a visa type selection
 * @param visaType - The selected visa type
 * @returns null if valid, error message if invalid
 */
export const validateVisaType = (visaType: string): string | null => {
  if (!visaType || visaType.trim().length === 0) {
    return i18n.t('validation.visa_type_required');
  }
  return null;
};

/**
 * Validates a location field
 * @param location - The location string (e.g., "San Francisco, CA")
 * @returns null if valid, error message if invalid
 */
export const validateLocation = (location: string): string | null => {
  if (!location || location.trim().length === 0) {
    return i18n.t('validation.location_required');
  }
  // Check for city and state format (e.g., "San Francisco, CA")
  if (!location.includes(',')) {
    return i18n.t('validation.location_format');
  }
  const parts = location.split(',').map(p => p.trim());
  if (parts.length < 2 || parts[0].length === 0 || parts[1].length === 0) {
    return i18n.t('validation.location_city_state');
  }
  return null;
};

/**
 * Validates language selections
 * @param languages - Array of selected languages
 * @returns null if valid, error message if invalid
 */
export const validateLanguages = (languages: string[]): string | null => {
  if (languages.length > 3) {
    return i18n.t('validation.languages_max_3');
  }
  return null;
};

/**
 * Validates that password and confirm password match
 * @param password - The password
 * @param confirmPassword - The confirmation password
 * @returns null if valid, error message if invalid
 */
export const validatePasswordMatch = (password: string, confirmPassword: string): string | null => {
  if (!confirmPassword || confirmPassword.length === 0) {
    return i18n.t('validation.confirm_password');
  }
  if (password !== confirmPassword) {
    return i18n.t('validation.passwords_no_match');
  }
  return null;
};
